// Vie du groupe (D-047) : défis et gages, comme dans Playbook.
//  - Défis : le staff les lance ; chacune note son résultat (ou celui d'une coéquipière) ;
//    classement, paliers, podium à la clôture ; défis collectifs.
//  - Défis enrichis (D-151, ce qui manquait par rapport à Playbook) : des idées de défis à lancer en un geste ; la façon
//    de noter (un chiffre, des séries « X essais × N séries », le chrono Start/Stop, le compte à rebours avec son « +1 ») ;
//    le score de chacune au choix (meilleur, moyenne, total, dernier) ; des paliers libres ; un défi réservé à une équipe
//    engagée (SF1 ou SF2) ; la courbe « Ma progression » ; les contributions de chacune dans un défi collectif ; le
//    classement remis à jour tout seul (toutes les 15 s, sans recharger) ; la clôture automatique après la date de fin
//    (la base) ; le podium en image (post ou story) pour les réseaux du club.
//  - Compléments (D-160) : le défi de présences, compté par l'app depuis l'appel des entraînements (le mois, le
//    trimestre ou la saison ; personne ne note rien ; « Corriger » pour le staff) ; le résumé du jour au coach (la base).
//  - Le défi Mains vives (D-165, là où le jeu caméra est ouvert) : rempli par le score d'une partie (le temps moyen ou les
//    touchées, sur un niveau, en 60 s) ; personne ne note à la main : « Jouer ce défi » ouvre le jeu, et la fin de la partie
//    propose « Envoyer mon score au défi » (le parent pour un mineur).
//  - Défis inter-catégories (D-095) : un coach, le responsable technique ou le bureau les lance pour
//    plusieurs catégories ; chaque autre catégorie part avec l'accord de son coach (carte d'accueil,
//    en un geste) ; un résultat compte une fois validé par le coach de sa catégorie (« Valider /
//    Refuser », « Tout valider ») ; participation de chaque catégorie, un podium par catégorie, « Tous »
//    entre catégories voisines en âge ; défi collectif : la jauge du club et la part de chaque catégorie.
//  - Gages (D-047, gardés simples par D-151) : les joueuses proposent, le staff valide (les idées sont celles de
//    l'équipe) ; le coach donne un tirage ; la joueuse tire au sort (3, 2, 1…), accepte ou passe (une dette) ; le staff
//    note quand c'est fait.
//  - Anniversaires (D-151) : le jour J, dans l'équipe (page de l'équipe, accueil), le prénom seulement, sans messages.
import './defis.css';
import {
  addChallengeEntry, addChallengeSeries, answerChallenge, birthdaysToday, challengeBoard, challengeClubTeams, challengeEntries, challengePeople,
  challengeReviewList, challenges, challengesByIds, closeChallenge, createChallenge, deleteChallenge, deleteChallengeEntry, launchInterChallenge,
  myBirthdays, pendingChallengeEntries, pendingChallengeTeams, reviewChallengeEntries, setBirthdayHidden,
  challengePresenceBoard, challengePresenceFix, challengePresencesSync, type PresenceBoardRow,
  deleteForfeitDraw, deleteForfeitIdea, forfeitDone, forfeitDraws, forfeitIdeaAuthors, forfeitIdeas, forfeitPlay, giveForfeit,
  proposeForfeit, setForfeitIdea, teamRoster,
  type Challenge, type ChallengeBoardRow, type ChallengeDraft, type ChallengeEntry, type ChallengePerson, type ClubTeamPick,
  type ForfeitDraw, type ForfeitIdea, type RosterEntry, type Team,
} from '../lib/api';
import {
  AGG_LABEL, ageRung, aggWord, canRankTogether, challengeIdeas, IDEA_THEMES, collective, contributors, fmtChrono, fmtScore, joinFr, MODE_LABEL, nextTier, pctOf,
  progression, ranking, rankingByTeam, rankNames, rulesLine, shortLabels, sortForPick, teamShares, tierClass, tiersOf,
  PERIOD_LABEL, periodText, presenceRange,
  type Aggregate, type ChallengeIdea, type ChallengeMode, type CountPeriod, type MvMeasure, type TierDef, type TierHit,
} from '../domain/challenges';
import { LEVELS, type Level } from '../domain/mains-vives';
import { isMinorPerson } from '../domain/minors';
import { agree, displayName, playerNoun, type TeamGender } from '../domain/labels';
import { parseLocalDate } from '../domain/schedule';
import { time } from './format';
import { hatShowing, isMinorAccount, modOn, mvOpen, switchHat, workTeams, type Ctx } from './context';
import { $, $$, confirmTwice, html, mount, openSheet, toast, type Html } from './dom';

type Seg = 'defis' | 'gages';
const num = (v: number) => v.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const fmtVal = (c: Challenge, v: number) => fmtScore(c, v);
const fmtThr = (c: Challenge, v: number) => (c.mode === 'chrono' ? fmtChrono(v) : c.mode === 'mainsvives' ? fmtScore(c, v) : num(v));
const tierChip = (h: TierHit | null) => { const k = tierClass(h); return k ? html`<span class="gl-tier gl-${k}">${h!.name}</span>` : ''; };
const shortDay = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
/** « Bronze 10 · Argent 15 · Or 18 » (au-delà de 4 paliers : « 6 paliers »). */
const tiersLine = (c: Challenge) => { const ts = tiersOf(c); return !ts.length ? '' : ts.length > 4 ? `${ts.length} paliers` : ts.map(t => `${t.name} ${fmtThr(c, t.threshold)}`).join(' · '); };
const rankTxt = (n: number) => `${n}${n === 1 ? 're' : 'e'}`;
/** « Ta moyenne », « Ton record »… ; pour un enfant ou plusieurs fiches : « Moyenne de Kenza ». */
const MY_WORD: Record<string, string> = { Meilleur: 'Ton meilleur', Moyenne: 'Ta moyenne', Total: 'Ton total', Dernier: 'Ton dernier', Record: 'Ton record', Présences: 'Tes présences' };
/** « hier 21h04 », « mardi 20h51 ». */
function whenShort(iso: string): string {
  const d = new Date(iso), now = new Date();
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((day(now) - day(d)) / 86400000);
  const label = diff <= 0 ? "aujourd'hui" : diff === 1 ? 'hier' : diff < 7 ? d.toLocaleDateString('fr-FR', { weekday: 'long' })
    : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  return `${label} ${time(d)}`;
}
/** Libellés courts (« U13 F », « SF ») des catégories d'un ou plusieurs défis. */
const boardLabels = (rows: ChallengeBoardRow[]) => shortLabels([...new Map(rows.map(r => [r.team_id,
  { id: r.team_id, name: r.name, short_name: r.short_name, category: r.category, gender: r.gender }])).values()]);
const partRow = (label: string, pct: number, txt: string) =>
  html`<div class="df-part-row"><b>${label}</b><span class="bk-bar"><span style="width:${pct}%"></span></span><span>${txt}</span></div>`;
/** Peut noter dans ce défi : tout le groupe, ou les joueuses de l'équipe engagée visée (celles qui jouent partout comprises). */
const inSquad = (c: Challenge, r: Pick<RosterEntry, 'squad_ids'> | undefined) => !c.squad_id || !r || !r.squad_ids?.length || r.squad_ids.includes(c.squad_id);

// Le classement à jour sans recharger (D-151) : l'écran relit les résultats toutes les 15 s tant qu'il est affiché.
let livePoll: number | undefined;

export async function renderGroupLife(root: HTMLElement, ctx: Ctx, teamId?: string, seg: Seg = 'defis'): Promise<void> {
  window.clearInterval(livePoll);
  const teams = workTeams(ctx);
  // Une notification vers une catégorie d'une autre casquette (D-072) : la casquette qui la montre.
  const otherHat = teamId && !teams.some(t => t.id === teamId) ? hatShowing(ctx, teamId) : null;
  if (otherHat) { switchHat(ctx, otherHat); window.dispatchEvent(new HashChangeEvent('hashchange')); return; }
  const team = teams.find(t => t.id === teamId) ?? teams[0];
  if (!team) { mount(root, html`<div class="empty"><p class="empty-title">Aucune équipe.</p></div>`); return; }
  const staff = ctx.staffOf.has(team.id);
  const roster = (await teamRoster(team.id).catch(() => [])).filter(r => r.kind === 'member');
  const nameOf = new Map(roster.map(r => [r.person_id, displayName(r)]));
  const mine = new Set(ctx.people.map(p => p.id));
  mount(root, html`
    <section class="screen gl">
      <header class="screen-head"><p class="eyebrow">${team.name}</p><h2>Défis et gages</h2></header>
      ${teams.length > 1 ? html`<nav class="team-strip" aria-label="Équipes">${teams.map(t => html`<a class="team-pill ${t.id === team.id ? 'on' : ''}" href="#/defis/${t.id}/${seg}">${t.name}</a>`)}</nav>` : ''}
      <nav class="seg bk-seg" aria-label="Défis et gages">
        <a href="#/defis/${team.id}/defis" ${seg === 'defis' ? html`aria-current="page"` : ''}>Défis</a>
        <a href="#/defis/${team.id}/gages" ${seg === 'gages' ? html`aria-current="page"` : ''} data-guide="df-gages">Gages</a>
      </nav>
      <div id="gl-body" class="screen"></div>
    </section>`);
  const body = $(root, '#gl-body')!;
  const reload = () => renderGroupLife(root, ctx, team.id, seg);
  if (seg === 'gages') return renderForfeits(body, team.id, staff, nameOf, mine, reload, new Set(ctx.people.filter(p => p.user_id !== ctx.uid).map(p => p.id)));
  return renderChallenges(body, ctx, team, staff, nameOf, mine, roster, reload);
}

// -----------------------------------------------------------------------------
// Défis
// -----------------------------------------------------------------------------
async function renderChallenges(el: HTMLElement, ctx: Ctx, team: Team, staff: boolean, nameOf: Map<string, string>, mine: Set<string>, roster: RosterEntry[], reload: () => void) {
  const rosterIds = roster.map(r => r.person_id);
  const rosterOf = new Map(roster.map(r => [r.person_id, r]));
  const squads = ctx.squads.filter(s => s.team_id === team.id);
  const squadName = (id: string | null) => (id ? squads.find(s => s.id === id)?.name ?? null : null);
  const list = await challenges(team.id).catch(() => [] as Challenge[]);
  // Les défis de présences (D-160) : le compte remis à jour à l'ouverture (la base le calcule depuis l'appel).
  if (list.some(c => c.mode === 'presences' && !c.closed_at && c.team_id === team.id)) await challengePresencesSync(team.id).catch(() => 0);
  const active = list.filter(c => c.team_status !== 'attente');
  const interIds = list.filter(c => c.inter).map(c => c.id);
  const [entries, board, people] = await Promise.all([
    challengeEntries(active.map(c => c.id)).catch(() => [] as ChallengeEntry[]),
    challengeBoard(interIds).catch(() => [] as ChallengeBoardRow[]),
    challengePeople(interIds).catch(() => [] as ChallengePerson[]),
  ]);
  const of = (c: Challenge) => entries.filter(e => e.challenge_id === c.id);
  const boardOf = (c: Challenge) => board.filter(b => b.challenge_id === c.id);
  const labels = boardLabels(board);
  const interName = rankNames(people);
  const nameFor = (id: string) => interName.get(id) ?? nameOf.get(id) ?? '—';
  const invites = list.filter(c => c.team_status === 'attente');
  const open = active.filter(c => !c.closed_at), closed = active.filter(c => c.closed_at);
  const myIds = rosterIds.filter(id => mine.has(id));
  // Un parent agit pour son enfant (D-103) : « Moyenne de Kenza », jamais « Ta moyenne ».
  const kids = new Set(ctx.people.filter(p => p.user_id !== ctx.uid).map(p => p.id));
  const canNote = (c: Challenge) => staff || myIds.some(id => inSquad(c, rosterOf.get(id)));
  const meLine = (c: Challenge, x: { person_id: string; best: number; rank: number; tier: TierHit | null }, of?: string) => {
    const who = myIds.length > 1 ? `${nameFor(x.person_id)} : ` : kids.has(x.person_id) ? `${aggWord(c)} de ${nameFor(x.person_id)} : ` : `${MY_WORD[aggWord(c)] ?? 'Ton score'} : `;
    const nx = c.kind === 'individual' ? nextTier(c, x.best) : null;
    return html`<p class="gl-me">${who}<strong>${c.mode === 'presences' ? num(x.best) : fmtVal(c, x.best)}</strong> · ${rankTxt(x.rank)}${of ? ` des ${of}` : ''} ${tierChip(x.tier)}
      ${nx && !c.closed_at ? html`<span class="df-next">prochain palier : ${nx.name} à ${fmtThr(c, nx.threshold)}</span>` : ''}</p>`;
  };

  const card = (c: Challenge) => {
    if (c.inter) return interCard(c);
    const es = of(c);
    const sq = squadName(c.squad_id);
    if (c.kind === 'collective') {
      const col = collective(c, es);
      const contrib = contributors(es);
      return html`<section class="block gl-card" data-guide="df-card">
        <div class="tpl-top"><span class="eyebrow eyebrow-accent">Défi collectif${sq ? ` · ${sq}` : ''}${c.ends_on ? ` · jusqu'au ${shortDay(c.ends_on)}` : ''}${c.closed_at ? ' · clos' : ''}</span>${col.done ? html`<span class="gl-tier gl-or">Réussi</span>` : ''}</div>
        <h3>${c.title}</h3>${c.description ? html`<p class="small">${c.description}</p>` : ''}
        <span class="bk-bar"><span style="width:${col.pct}%"></span></span>
        <p class="small"><strong>${fmtVal(c, col.total)}</strong> sur ${fmtVal(c, c.target ?? 0)} · ${col.pct} % · ${es.length} contribution${es.length > 1 ? 's' : ''}</p>
        ${contrib.length ? html`<details class="df-contrib" ${contrib.length <= 4 ? 'open' : ''}><summary>Qui a contribué · ${contrib.length}</summary>
          ${contrib.map(x => partRow(nameFor(x.person_id), x.pct, `${fmtVal(c, x.total)} · ${x.pct} %`))}</details>` : ''}
        ${actions(c)}</section>`;
    }
    const r = ranking(c, es);
    const me = r.filter(x => mine.has(x.person_id));
    return html`<section class="block gl-card" data-guide="df-card">
      <div class="tpl-top"><span class="eyebrow eyebrow-accent">${c.mode === 'presences' ? 'Présences à l’entraînement' : c.mode === 'simple' ? (c.better === 'lower' ? 'Au chrono' : 'Défi') : MODE_LABEL[c.mode]}${sq ? ` · ${sq}` : ''}${c.ends_on ? ` · jusqu'au ${shortDay(c.ends_on)}` : ''}${c.closed_at ? ' · clos' : ''}</span>
        <span class="muted small">${tiersLine(c)}</span></div>
      <h3>${c.title}</h3>${c.description ? html`<p class="small">${c.description}</p>` : ''}
      <p class="muted small df-rules">${rulesLine(c)}</p>
      ${me.map(x => meLine(c, x))}
      ${r.length ? html`<ol class="gl-rank">${r.slice(0, c.closed_at ? 3 : 5).map(x => html`<li class="${mine.has(x.person_id) ? 'me' : ''}">
        <b>${x.rank}</b><span>${nameOf.get(x.person_id) ?? '—'}</span>${tierChip(x.tier) || html`<span></span>`}<b>${fmtVal(c, x.best)}</b></li>`)}</ol>`
        : html`<p class="muted small">${c.mode === 'presences' ? 'Le compte démarre après le premier entraînement.' : c.mode === 'mainsvives' ? 'Personne encore : une partie de Mains vives, puis le score s’envoie au défi depuis le jeu.' : 'Personne encore : à toi de lancer le mouvement !'}</p>`}
      ${actions(c, me.length ? me[0]!.person_id : null)}</section>`;
  };
  // Un défi Mains vives (D-165) : « Jouer ce défi » ouvre le jeu au niveau et à la durée du défi ; un mineur joue avec le
  // téléphone d'un parent (sur le compte du parent : « La séance de Kenza »).
  const mvPlay = (c: Challenge): Html | '' => {
    if (c.mode !== 'mainsvives' || c.closed_at || !mvOpen(ctx, team.club_id)) return '';
    const pid = myIds.find(id => inSquad(c, rosterOf.get(id)) && (!kids.has(id) || isMinorPerson(ctx.people.find(p => p.id === id))));
    if (!pid) return '';
    if (!kids.has(pid) && isMinorAccount(ctx)) return html`<span class="muted small">Avec le téléphone d’un parent</span>`;
    return html`<a class="btn btn-primary btn-sm" href="${kids.has(pid) ? `#/mains-vives/pour/${pid}/defi/${c.id}` : `#/mains-vives/defi/${c.id}`}" data-guide="df-mv-play">▶ ${kids.has(pid) ? `La séance de ${nameFor(pid)}` : 'Jouer ce défi'}</a>`;
  };
  const actions = (c: Challenge, progFor: string | null = null) => {
    // Un défi de présences : rien à noter, pas de courbe ; le staff corrige. Un défi Mains vives : rien à noter non plus.
    const auto = c.mode === 'presences';
    const mvc = c.mode === 'mainsvives';
    const prog = progFor && c.kind === 'individual' && !auto ? html`<button type="button" class="btn btn-ghost btn-sm" data-ch-prog="${c.id}" data-person="${progFor}">Ma progression</button>` : '';
    const share = staff && c.closed_at && of(c).length ? html`<button type="button" class="btn btn-ghost btn-sm" data-ch-share="${c.id}">${c.kind === 'collective' ? 'Partager en image' : 'Partager le podium'}</button>` : '';
    if (c.closed_at) return html`${prog || share ? html`<div class="btn-row">${prog}${share}</div>` : ''}${staff ? html`<button type="button" class="link-x" data-ch-del="${c.id}">supprimer le défi</button>` : ''}`;
    if (auto && !staff) return '';
    return html`<div class="btn-row">${canNote(c) && !auto && !mvc ? html`<button type="button" class="btn btn-primary btn-sm" data-ch-add="${c.id}" data-guide="df-note">Noter un résultat</button>` : ''}${mvPlay(c)}${prog}
      ${auto ? html`<button type="button" class="btn btn-primary btn-sm" data-ch-fix="${c.id}" data-guide="df-fix">Corriger</button>` : ''}
      ${staff ? html`<button type="button" class="btn btn-ghost btn-sm" data-ch-close="${c.id}">${c.kind === 'collective' ? 'Clore le défi' : 'Clore (podium)'}</button>` : ''}</div>`;
  };

  // Défi inter-catégories (D-095) : participation, un podium par catégorie (« Tous » entre catégories voisines),
  // ou la jauge du club ; les résultats qui attendent leur coach ; « Valider » pour le coach de cette catégorie.
  const interCard = (c: Challenge) => {
    const es = of(c), rows = boardOf(c);
    const live = rows.filter(r => r.status === 'accepte');
    const here = live.find(r => r.team_id === team.id);
    const lab = (id: string) => labels.get(id) ?? rows.find(r => r.team_id === id)?.name ?? '';
    const ordered = [...live].sort((a, b) => Number(b.team_id === team.id) - Number(a.team_id === team.id));
    const later = (s: ChallengeBoardRow['status'], t: string) => { const l = rows.filter(r => r.status === s); return l.length ? `${t} : ${l.map(r => lab(r.team_id)).join(', ')}` : ''; };
    const status = staff ? [later('attente', 'En attente de leur coach'), later('refuse', 'Pas cette fois'), later('expire', 'Sans réponse')].filter(Boolean).join(' · ') : '';
    const head = html`
      <div class="tpl-top"><span class="eyebrow eyebrow-accent">${c.kind === 'collective' ? 'Défi collectif inter-catégories' : `Défi inter-catégories${c.mode !== 'simple' ? ` · ${MODE_LABEL[c.mode].toLowerCase()}` : c.better === 'lower' ? ' · au chrono' : ''}`}${c.ends_on ? ` · jusqu'au ${shortDay(c.ends_on)}` : ''}${c.closed_at ? ' · clos' : ''}</span>
        ${c.kind === 'individual' ? html`<span class="muted small">${tiersLine(c)}</span>` : ''}</div>
      <h3>${c.title}</h3>${c.description ? html`<p class="small">${c.description}</p>` : ''}
      ${c.kind === 'individual' && c.mode !== 'simple' ? html`<p class="muted small df-rules">${rulesLine(c)}</p>` : ''}
      <p class="small">Proposé par ${rows[0]?.launcher ?? '—'} · ${live.map(r => r.name).join(', ')}</p>
      ${status ? html`<p class="muted small">${status}</p>` : ''}`;
    let body: Html;
    if (c.kind === 'collective') {
      const total = live.reduce((a, r) => a + Number(r.total), 0);
      const pct = pctOf(total, c.target ?? 0);
      const shares = teamShares(live.map(r => ({ team_id: r.team_id, total: Number(r.total) }))).sort((a, b) => b.total - a.total);
      body = html`
        <div class="df-part"><p class="eyebrow">Jauge du club</p>
          <span class="bk-bar df-gauge"><span style="width:${pct}%"></span></span>
          <p class="small"><strong>${fmtVal(c, total)}</strong> sur ${fmtVal(c, c.target ?? 0)} · ${pct} %${c.target && total >= c.target ? ' · objectif atteint !' : ''}</p></div>
        <div class="df-part"><p class="eyebrow">Part de chaque catégorie</p>
          ${shares.map(s => partRow(lab(s.team_id), s.pct, `${num(s.total)} · ${s.pct} %`))}</div>`;
    } else {
      const by = rankingByTeam(c, es);
      const together = canRankTogether(live.map(r => r.category));
      const all = together ? ranking(c, es) : [];
      const teamOfPerson = new Map(es.map(e => [e.person_id, e.team_id]));
      // Le podium, et ma place si je suis plus loin.
      const podium = <T extends { person_id: string }>(rs: T[], n: number) => [...rs.slice(0, n), ...rs.slice(n).filter(x => mine.has(x.person_id))];
      const li = (x: ReturnType<typeof ranking>[number], withCat = false) => html`<li class="${mine.has(x.person_id) ? 'me' : ''}"><b>${x.rank}</b>
        <span>${nameFor(x.person_id)}${withCat ? html` <span class="df-cat">${lab(teamOfPerson.get(x.person_id) ?? '')}</span>` : ''}</span>${tierChip(x.tier) || html`<span></span>`}<b>${fmtVal(c, x.best)}</b></li>`;
      const me = (by.get(team.id) ?? []).filter(x => mine.has(x.person_id));
      const myPending = es.filter(e => mine.has(e.person_id) && e.status === 'attente').length;
      const part = [...live].sort((a, b) => pctOf(b.participants, b.members) - pctOf(a.participants, a.members));
      body = html`
        ${me.map(x => meLine(c, x, lab(team.id)))}
        ${myPending ? html`<p class="small"><span class="df-pend">${myPending > 1 ? `${myPending} résultats en attente de validation` : myIds.length === 1 && kids.has(myIds[0]!) ? `Le résultat de ${nameFor(myIds[0]!)} attend la validation du coach` : 'Ton résultat attend la validation du coach'}</span></p>` : ''}
        <div class="df-part"><p class="eyebrow">Participation</p>
          ${part.map(r => partRow(lab(r.team_id), pctOf(r.participants, r.members), `${r.participants} / ${r.members} · ${pctOf(r.participants, r.members)} %`))}</div>
        ${together ? html`<div class="seg df-view" role="radiogroup" aria-label="Classement">
          <button type="button" role="radio" data-df-view="cat" aria-pressed="true">Par catégorie</button>
          <button type="button" role="radio" data-df-view="all" aria-pressed="false">Tous</button></div>` : ''}
        <div class="df-pods" data-df-pane="cat">${ordered.map(r => {
          const rs = by.get(r.team_id) ?? [];
          return html`<p class="eyebrow eyebrow-accent df-pod">${r.name}</p>${rs.length
            ? html`<ol class="gl-rank">${podium(rs, 3).map(x => li(x))}</ol>` : html`<p class="muted small">Pas encore de résultat validé.</p>`}`;
        })}</div>
        ${together ? html`<div class="df-pods" data-df-pane="all" hidden>${all.length
          ? html`<ol class="gl-rank">${podium(all, c.closed_at ? 3 : 10).map(x => li(x, true))}</ol>` : html`<p class="muted small">Pas encore de résultat validé.</p>`}</div>` : ''}`;
    }
    const pendingAll = live.reduce((a, r) => a + r.pending, 0);
    const wait = pendingAll ? html`<div class="df-wait"><span aria-hidden="true">⏳</span><span>${pendingAll > 1 ? `${pendingAll} résultats attendent` : '1 résultat attend'} la validation de leur coach</span></div>` : '';
    const check = here?.mine && here.pending ? html`<button type="button" class="btn btn-ghost btn-sm" data-df-check="${c.id}">Valider (${here.pending})</button>` : '';
    const hostStaff = staff && c.team_id === team.id;
    const myFirst = c.kind === 'individual' ? es.find(e => mine.has(e.person_id) && e.status === 'valide')?.person_id ?? null : null;
    const prog = myFirst ? html`<button type="button" class="btn btn-ghost btn-sm" data-ch-prog="${c.id}" data-person="${myFirst}">Ma progression</button>` : '';
    const acts = c.closed_at
      ? html`${check || prog ? html`<div class="btn-row">${check}${prog}</div>` : ''}${hostStaff ? html`<button type="button" class="link-x" data-ch-del="${c.id}">supprimer le défi</button>` : ''}`
      : html`<div class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-ch-add="${c.id}">Noter un résultat</button>${check}${prog}
          ${hostStaff ? html`<button type="button" class="btn btn-ghost btn-sm" data-ch-close="${c.id}">${c.kind === 'collective' ? 'Clore le défi' : 'Clore (podiums)'}</button>` : ''}</div>`;
    return html`<section class="block gl-card df-card">${head}${body}${wait}${acts}</section>`;
  };

  mount(el, html`
    ${invites.map(c => inviteCard(c, boardOf(c), team.id))}
    ${staff ? html`<button type="button" class="btn btn-primary vol-ask" id="gl-new" data-guide="df-new">＋ Lancer un défi</button>` : ''}
    ${open.length ? open.map(card) : html`<div class="empty"><p class="empty-title">Pas de défi en cours</p><p>${staff ? 'Touche « ＋ Lancer un défi » : des idées sont prêtes (lancers francs 5 × 5, sprint au chrono, 500 pompes en équipe…).' : 'Ton coach en lancera bientôt.'}</p></div>`}
    ${closed.length ? html`<h3 class="hub-h">Podiums</h3>${closed.slice(0, 6).map(card)}` : ''}`);

  wireAnswers(el, reload);
  $(el, '#gl-new')?.addEventListener('click', () => void openNewChallenge(ctx, team, reload));
  // « Par catégorie | Tous » (défi inter-catégories entre catégories voisines).
  for (const b of $$<HTMLButtonElement>(el, '[data-df-view]')) b.addEventListener('click', () => {
    const cardEl = b.closest('.df-card')!;
    $$(cardEl, '[data-df-view]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
    $$(cardEl, '[data-df-pane]').forEach(p => { p.hidden = p.dataset.dfPane !== b.dataset.dfView; });
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-df-check]')) b.addEventListener('click', () => {
    const c = list.find(x => x.id === b.dataset.dfCheck)!;
    void openReview(c, team, reload);
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ch-prog]')) b.addEventListener('click', () => {
    const c = list.find(x => x.id === b.dataset.chProg)!;
    openProgress(c, of(c), b.dataset.person!, nameFor(b.dataset.person!), myIds.length > 1 || kids.has(b.dataset.person!));
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ch-fix]')) b.addEventListener('click', () => {
    const c = list.find(x => x.id === b.dataset.chFix)!;
    void openPresenceFix(c, team, nameOf, reload);
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ch-share]')) b.addEventListener('click', () => {
    const c = list.find(x => x.id === b.dataset.chShare)!;
    openShareImage(ctx, team, c, of(c), nameFor);
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ch-add]')) b.addEventListener('click', () => {
    const c = list.find(x => x.id === b.dataset.chAdd)!;
    const here = boardOf(c).find(r => r.team_id === team.id);
    openEntry({ c, team, staff, here: here?.mine ?? false, nameOf, rosterIds: rosterIds.filter(id => inSquad(c, rosterOf.get(id))), myIds,
      kids, myRes: of(c).filter(e => mine.has(e.person_id) && (!c.inter || e.team_id === team.id)), onDone: reload });
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ch-close]')) confirmTwice(b, 'Confirmer : clore et afficher le podium', async () => {
    try { await closeChallenge(b.dataset.chClose!); toast('Défi clos : le podium est affiché, l’équipe est prévenue'); reload(); } catch { toast('Impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-ch-del]')) confirmTwice(b, 'confirmer la suppression', async () => {
    try { await deleteChallenge(b.dataset.chDel!); reload(); } catch { toast('Impossible.', 'ko'); }
  });

  // Le classement à jour sans recharger : les résultats relus toutes les 15 s ; l'écran redessiné s'ils ont changé,
  // jamais pendant une fiche ouverte (la saisie en cours n'est pas perdue).
  const sig = (es: ChallengeEntry[]) => es.map(e => `${e.id}:${e.status}`).join(',');
  const shown = sig(entries);
  const hash = location.hash;
  if (open.length) livePoll = window.setInterval(() => {
    if (location.hash !== hash || !el.isConnected) { window.clearInterval(livePoll); return; }
    if (document.visibilityState !== 'visible' || document.querySelector('.sheet, .pk-over')) return;
    void challengeEntries(active.map(c => c.id)).then(es => { if (sig(es) !== shown && el.isConnected && location.hash === hash) reload(); }).catch(() => undefined);
  }, 15000);
}

// -----------------------------------------------------------------------------
// Noter un résultat : un chiffre, des séries, le chrono, le compte à rebours
// -----------------------------------------------------------------------------
interface EntryCtx {
  c: Challenge; team: Team; staff: boolean; here: boolean; nameOf: Map<string, string>; rosterIds: string[]; myIds: string[]; kids: Set<string>;
  myRes: ChallengeEntry[]; onDone: () => void;
}
function openEntry(x: EntryCtx) {
  const { c, team, nameOf, myIds, kids } = x;
  const g = team.gender;
  const unit = c.unit ? ` (${c.unit})` : '';
  const state = (e: ChallengeEntry) => (!c.inter ? '' : e.status === 'valide' ? html` <span class="chip chip-ok">validé</span>`
    : e.status === 'attente' ? html` <span class="df-pend">en attente de validation</span>` : html` <span class="chip chip-ko">refusé</span>`);
  const how = c.mode === 'series' ? `Chaque série compte (${aggWord(c).toLowerCase()} des séries).` : c.mode === 'chrono' ? 'Au chrono : le plus court gagne.'
    : c.mode === 'rebours' ? `Le plus possible en ${c.countdown_s ?? 60} s.` : c.better === 'lower' ? 'Au chrono : le plus court gagne.'
    : (c.aggregate ?? 'best') === 'best' ? 'Le meilleur résultat compte.' : `${AGG_LABEL[c.aggregate]} de tes résultats.`;
  const hint = c.inter
    ? (x.here ? 'Défi inter-catégories : saisi par toi, le résultat est validé d’office.' : 'Défi inter-catégories : ton résultat compte dès que ton coach l’a validé.')
    : `${c.kind === 'collective' ? 'Chaque contribution s’ajoute au total.' : how} ${agree(g, 'Chacun voit qui a saisi.', 'Chacune voit qui a saisi.')}`;
  const n = Math.max(1, Math.min(20, c.series_count ?? 1)), size = c.series_size ?? 10;
  const seriesRow = (i: number) => html`<div class="df-serie"><span>Série ${i + 1}</span>
    <input name="made" type="number" inputmode="numeric" min="0" step="1" aria-label="Série ${i + 1} : réussis">
    <span>sur</span><input name="att" type="number" inputmode="numeric" min="1" step="1" value="${size}" aria-label="Série ${i + 1} : tentatives"></div>`;
  const field = c.mode === 'series' ? html`
      <div class="field"><span>Réussis${unit}</span><div class="df-series" id="df-series">${Array.from({ length: n }, (_, i) => seriesRow(i))}</div>
        <button type="button" class="link-x df-more" id="df-more">＋ une série</button></div>`
    : c.mode === 'chrono' ? html`
      <div class="df-clock"><output class="df-big" id="df-time" aria-live="off">0,00 s</output>
        <div class="btn-row"><button type="button" class="btn btn-primary" id="df-go">Départ</button><button type="button" class="btn btn-ghost" id="df-zero">Remettre à zéro</button></div></div>
      <label class="field"><span>Temps (secondes)</span><input name="value" type="number" inputmode="decimal" min="0.01" step="0.01" required></label>`
    : c.mode === 'rebours' ? html`
      <div class="df-clock"><output class="df-big" id="df-left" aria-live="off">${c.countdown_s ?? 60}</output><p class="muted small" id="df-left-s">secondes</p>
        <button type="button" class="btn btn-primary" id="df-go">Départ</button>
        <button type="button" class="df-plus" id="df-plus" hidden>＋1</button>
        <p class="small" id="df-count" hidden></p></div>
      <label class="field"><span>Réussis${unit}</span><input name="value" type="number" inputmode="numeric" min="0" step="1" required></label>`
    : html`<label class="field"><span>${c.kind === 'collective' ? 'Contribution' : 'Résultat'}${unit}</span><input name="value" type="number" inputmode="decimal" min="0" step="any" required></label>`;
  let timer = 0;
  openSheet(c.title, html`
    <form class="form" id="f-ce" novalidate>
      <label class="field"><span>Pour qui</span><select name="person">${x.rosterIds.map(id => html`<option value="${id}" ${myIds[0] === id ? 'selected' : ''}>${nameOf.get(id)}</option>`)}</select></label>
      ${field}
      <label class="field"><span>Note (facultatif)</span><input name="note" maxlength="120" placeholder="ex. après l'entraînement"></label>
      <p class="hint">${hint}</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">Enregistrer</button>
    </form>
    ${x.myRes.length ? html`<section class="sheet-sec"><h4>${myIds.length === 1 && kids.has(myIds[0]!) ? `Résultats de ${nameOf.get(myIds[0]!)}` : 'Mes résultats'}</h4><ul class="ce-rows">${[...x.myRes].reverse().slice(0, 30).map(e => html`
      <li><span>${c.mode === 'series' && e.made != null ? `${num(Number(e.made))} sur ${num(Number(e.attempts ?? size))}` : fmtVal(c, Number(e.value))}${state(e)}${e.note ? html` <span class="muted small">· ${e.note}</span>` : ''}</span>
        ${!c.inter || e.status !== 'valide' || x.staff ? html`<button type="button" class="link-x" data-ce-del="${e.id}">retirer</button>` : ''}</li>`)}</ul></section>` : ''}`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ce')!;
    const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
    const valueIn = $<HTMLInputElement>(f, 'input[name="value"]');
    // Le chrono : Départ / Stop, le temps passe dans le champ (corrigeable : panier au buzzer).
    if (c.mode === 'chrono') {
      const out = $(f, '#df-time')!, go = $<HTMLButtonElement>(f, '#df-go')!;
      let t0 = 0;
      // Le chrono qui tourne : toujours deux décimales (« 4,60 s », « 1:02,35 »), le chiffre ne saute pas.
      const show = (s: number) => { const m = Math.floor(s / 60), r = (s - m * 60).toFixed(2).replace('.', ','); out.textContent = m ? `${m}:${r.padStart(5, '0')}` : `${r} s`; };
      const frame = () => { show((performance.now() - t0) / 1000); timer = requestAnimationFrame(frame); };
      go.addEventListener('click', () => {
        if (!t0) { t0 = performance.now(); go.textContent = 'Stop'; timer = requestAnimationFrame(frame); return; }
        cancelAnimationFrame(timer);
        const s = Math.round((performance.now() - t0) / 10) / 100;
        t0 = 0; go.textContent = 'Départ'; show(s);
        if (valueIn) valueIn.value = String(s);
      });
      $(f, '#df-zero')!.addEventListener('click', () => { cancelAnimationFrame(timer); t0 = 0; go.textContent = 'Départ'; show(0); if (valueIn) valueIn.value = ''; });
    }
    // Le compte à rebours : Départ, un grand « +1 » à chaque réussite ; à zéro, le compte passe dans le champ.
    if (c.mode === 'rebours') {
      const total = c.countdown_s ?? 60;
      const left = $(f, '#df-left')!, go = $<HTMLButtonElement>(f, '#df-go')!, plus = $<HTMLButtonElement>(f, '#df-plus')!, count = $(f, '#df-count')!;
      let n2 = 0, end = 0;
      const say = () => { count.textContent = `${n2} ${c.unit ?? 'réussis'}`; if (valueIn) valueIn.value = String(n2); };
      const stop = () => { window.clearInterval(timer); plus.hidden = true; go.hidden = false; go.textContent = 'Recommencer'; left.textContent = '0'; navigator.vibrate?.(300); say(); };
      go.addEventListener('click', () => {
        n2 = 0; say(); count.hidden = false; plus.hidden = false; go.hidden = true;
        end = performance.now() + total * 1000;
        timer = window.setInterval(() => { const s = Math.ceil((end - performance.now()) / 1000); if (s <= 0) stop(); else left.textContent = String(s); }, 200);
      });
      plus.addEventListener('click', () => { n2 += 1; say(); });
    }
    $(f, '#df-more')?.addEventListener('click', (e) => {
      const box = $(f, '#df-series')!;
      const k = $$(box, '.df-serie').length;
      if (k >= 20) { (e.currentTarget as HTMLElement).hidden = true; return; }
      box.insertAdjacentHTML('beforeend', seriesRow(k).s);
    });
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const person = String(d.get('person'));
      const note = String(d.get('note') ?? '').trim() || null;
      const teamArg = c.inter ? team.id : undefined;
      try {
        if (c.mode === 'series') {
          const made = d.getAll('made').map(v => String(v).trim()), att = d.getAll('att').map(v => Number(String(v).replace(',', '.')));
          const rows = made.map((m, i) => ({ made: Number(m.replace(',', '.')), attempts: att[i] || size, empty: m === '' })).filter(r => !r.empty);
          if (!rows.length) return msg('Indique les réussis d’au moins une série.');
          if (rows.some(r => !(r.made >= 0) || !(r.attempts >= 1) || r.made > r.attempts)) return msg('Une série a plus de réussis que de tentatives.');
          await addChallengeSeries(c.id, person, rows.map(r => ({ made: r.made, attempts: r.attempts })), note, teamArg);
        } else {
          const raw = String(d.get('value') ?? '').trim();
          const v = Number(raw.replace(',', '.'));
          if (raw === '' || !(v >= 0) || (c.mode === 'chrono' && v <= 0)) return msg(c.mode === 'chrono' ? 'Lance le chrono, ou indique un temps.' : 'Indique un résultat.');
          await addChallengeEntry(c.id, person, v, note, teamArg);
        }
        close(); toast(c.inter && !x.here ? 'Résultat noté : ton coach le valide' : 'Résultat noté'); x.onDone();
      } catch { msg('Impossible pour le moment.'); }
    });
    for (const b2 of $$<HTMLButtonElement>(sheet, '[data-ce-del]')) b2.addEventListener('click', async () => {
      try { await deleteChallengeEntry(b2.dataset.ceDel!); close(); x.onDone(); } catch { toast('Impossible.', 'ko'); }
    });
  }, () => { cancelAnimationFrame(timer); window.clearInterval(timer); });
}

// -----------------------------------------------------------------------------
// Ma progression : chaque résultat, la courbe, le prochain palier
// -----------------------------------------------------------------------------
function openProgress(c: Challenge, es: ChallengeEntry[], personId: string, name: string, other: boolean) {
  const pts = progression(c, es, personId);
  const last = pts[pts.length - 1];
  const nx = last && c.kind === 'individual' ? nextTier(c, last.score) : null;
  const W = 320, H = 150, L = 34, R = 10, T = 12, B = 22;
  const vals = pts.map(p => p.value);
  const lo0 = Math.min(...vals, nx ? nx.threshold : Infinity), hi0 = Math.max(...vals, nx ? nx.threshold : -Infinity);
  const pad = (hi0 - lo0) * 0.12 || Math.max(1, Math.abs(hi0) * 0.1);
  const lo = Math.max(0, lo0 - pad), hi = hi0 + pad;
  const X = (i: number) => (pts.length === 1 ? (L + W - R) / 2 : L + (i * (W - L - R)) / (pts.length - 1));
  const Y = (v: number) => T + (1 - (v - lo) / (hi - lo || 1)) * (H - T - B);
  const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '');
  const best = c.better === 'lower' ? Math.min(...vals) : Math.max(...vals);
  const chart = pts.length ? html`<svg class="df-chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Ma progression : ${pts.length} résultat${pts.length > 1 ? 's' : ''}">
    <line class="df-ax" x1="${L}" y1="${H - B}" x2="${W - R}" y2="${H - B}"></line>
    <text class="df-tk" x="${L - 6}" y="${Y(hi) + 4}" text-anchor="end">${fmtThr(c, hi)}</text>
    <text class="df-tk" x="${L - 6}" y="${Y(lo) + 4}" text-anchor="end">${fmtThr(c, lo)}</text>
    ${nx ? html`<line class="df-goal" x1="${L}" y1="${Y(nx.threshold)}" x2="${W - R}" y2="${Y(nx.threshold)}"></line>
      <text class="df-tk df-goal-t" x="${W - R}" y="${Y(nx.threshold) - 4}" text-anchor="end">${nx.name} ${fmtThr(c, nx.threshold)}</text>` : ''}
    ${pts.length > 1 ? html`<polyline class="df-line" points="${pts.map((p, i) => `${X(i).toFixed(1)},${Y(p.value).toFixed(1)}`).join(' ')}"></polyline>` : ''}
    ${pts.map((p, i) => html`<circle class="df-dot ${p.value === best ? 'df-best' : ''}" cx="${X(i).toFixed(1)}" cy="${Y(p.value).toFixed(1)}" r="4"><title>${fmtVal(c, p.value)}${p.at ? ` · ${day(p.at)}` : ''}</title></circle>`)}
    <text class="df-tk" x="${L}" y="${H - 6}">${day(pts[0]!.at)}</text>
    ${pts.length > 1 ? html`<text class="df-tk" x="${W - R}" y="${H - 6}" text-anchor="end">${day(last!.at)}</text>` : ''}
  </svg>` : '';
  openSheet(other ? `Progression de ${name}` : 'Ma progression', html`<div class="df-prog">
    <p class="eyebrow eyebrow-accent">${c.title}</p>
    ${last ? html`<p class="lead">${other ? `${aggWord(c)} : ` : `${MY_WORD[aggWord(c)] ?? 'Ton score'} : `}<strong>${fmtVal(c, last.score)}</strong> · ${pts.length} résultat${pts.length > 1 ? 's' : ''}${nx ? ` · prochain palier : ${nx.name} à ${fmtThr(c, nx.threshold)}` : ''}</p>` : html`<p class="muted">Pas encore de résultat.</p>`}
    ${chart}
    ${c.better === 'lower' ? html`<p class="hint">Au chrono : plus le point est bas, plus c’est rapide.</p>` : ''}
    ${pts.length ? html`<ul class="ce-rows df-prog-list">${[...pts].reverse().map(p => html`<li><span>${fmtVal(c, p.value)}${p.value === best ? html` <span class="gl-tier gl-or">record</span>` : ''}</span><span class="muted small">${p.at ? whenShort(p.at) : ''}</span></li>`)}</ul>` : ''}
  </div>`);
}

// -----------------------------------------------------------------------------
// Corriger un défi de présences (staff) : le compte de l'app, une correction + / − par personne
// -----------------------------------------------------------------------------
async function openPresenceFix(c: Challenge, team: Team, nameOf: Map<string, string>, onDone: () => void) {
  const rows = (await challengePresenceBoard(c.id).catch(() => [] as PresenceBoardRow[]))
    .sort((a, b) => (nameOf.get(a.person_id) ?? '').localeCompare(nameOf.get(b.person_id) ?? '', 'fr'));
  const g = team.gender;
  const delta = new Map(rows.map(r => [r.person_id, r.delta]));
  const sign = (n: number) => (n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '0');
  openSheet('Corriger les présences', html`<div class="df-fixes">
    <p class="lead">${c.title}${c.count_from && c.ends_on ? ` · ${periodText(c.count_from, c.ends_on)}` : ''}</p>
    <p class="hint">L’app compte chaque entraînement fini d’après l’appel : ${agree(g, 'présent', 'présente')} par défaut, sauf absence notée. Une présence oubliée, une séance avec une autre équipe : corrige ici. L’équipe ne voit que le total.</p>
    ${rows.length ? rows.map(r => html`<div class="df-fix" data-p="${r.person_id}">
      <span class="df-fix-n"><b>${nameOf.get(r.person_id) ?? '—'}</b><small>compté par l’app : ${r.counted}</small></span>
      <span class="df-step" role="group" aria-label="Correction pour ${nameOf.get(r.person_id) ?? ''}">
        <button type="button" data-d="-1" aria-label="Une présence de moins">−</button><output>${sign(r.delta)}</output><button type="button" data-d="1" aria-label="Une présence de plus">+</button></span>
      <b class="df-fix-t">${Math.max(0, r.counted + r.delta)}</b></div>`)
      : html`<p class="muted small">Personne dans ce défi.</p>`}
    ${rows.length ? html`<button type="button" class="btn btn-primary" id="df-fix-save" disabled>Enregistrer</button>` : ''}
  </div>`, (sheet, close) => {
    const save = $<HTMLButtonElement>(sheet, '#df-fix-save');
    const changed = () => rows.filter(r => (delta.get(r.person_id) ?? 0) !== r.delta);
    for (const b of $$<HTMLButtonElement>(sheet, '[data-d]')) b.addEventListener('click', () => {
      const row = b.closest<HTMLElement>('.df-fix')!, id = row.dataset.p!;
      const r = rows.find(x => x.person_id === id)!;
      const d = Math.max(-r.counted, Math.min(500, (delta.get(id) ?? 0) + Number(b.dataset.d)));
      delta.set(id, d);
      $(row, 'output')!.textContent = sign(d);
      $(row, '.df-fix-t')!.textContent = String(Math.max(0, r.counted + d));
      row.classList.toggle('on', d !== 0);
      if (save) { const n = changed().length; save.disabled = !n; save.textContent = n ? `Enregistrer · ${n} correction${n > 1 ? 's' : ''}` : 'Enregistrer'; }
    });
    for (const row of $$(sheet, '.df-fix')) row.classList.toggle('on', (delta.get(row.dataset.p!) ?? 0) !== 0);
    save?.addEventListener('click', async () => {
      save.disabled = true;
      try {
        for (const r of changed()) await challengePresenceFix(c.id, r.person_id, delta.get(r.person_id) ?? 0);
        close(); toast('Présences corrigées : le classement suit'); onDone();
      } catch { save.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    });
  });
}

// -----------------------------------------------------------------------------
// Le podium en image, pour les réseaux du club (post 4:5 ou story 9:16)
// -----------------------------------------------------------------------------
const loadImg = (src: string) => new Promise<HTMLImageElement>((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = src; });
function openShareImage(ctx: Ctx, team: Team, c: Challenge, es: ChallengeEntry[], nameFor: (id: string) => string) {
  // Mineurs (D-103) : sur une image publique, l'initiale seulement hors des équipes seniors.
  const adult = ageRung(team.category) === 7;
  const who = (id: string) => { const n = nameFor(id); return adult ? n : `${n.charAt(0).toUpperCase()}.`; };
  openSheet('Partager en image', html`<div class="form">
    <p>Aux couleurs du club, prête pour Instagram ou WhatsApp.${adult ? '' : ' Les prénoms n’y figurent pas (équipe de jeunes) : seulement l’initiale.'}</p>
    <div class="btn-row"><button type="button" class="btn btn-primary" data-fmt="post">Post (4:5)</button><button type="button" class="btn btn-ghost" data-fmt="story">Story (9:16)</button></div>
  </div>`, (sheet) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-fmt]')) b.addEventListener('click', async () => {
      b.disabled = true;
      try {
        const blob = await drawPodium(ctx, team, c, es, who, b.dataset.fmt === 'story');
        const file = new File([blob], `defi-${c.title.normalize('NFD').replace(/[^\w]+/g, '-').toLowerCase().slice(0, 40)}.png`, { type: 'image/png' });
        if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: c.title }); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = file.name;
        document.body.append(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30000);
        toast('Image enregistrée');
      } catch (e) { if ((e as Error).name !== 'AbortError') toast('Image impossible pour le moment.', 'ko'); }
      finally { b.disabled = false; }
    });
  });
}

async function drawPodium(ctx: Ctx, team: Team, c: Challenge, es: ChallengeEntry[], who: (id: string) => string, story: boolean): Promise<Blob> {
  const W = 1080, H = story ? 1920 : 1350;
  const cv = document.createElement('canvas');
  cv.width = W; cv.height = H;
  const g = cv.getContext('2d')!;
  const shown = getComputedStyle(document.documentElement).getPropertyValue('--club-l-fill').trim();
  const color = [ctx.club?.primary_color ?? '', shown].find(x => /^#[0-9a-f]{6}$/i.test(x)) ?? '#C41E3A';
  await Promise.all([document.fonts.load('400 120px Anton'), document.fonts.load('700 40px "JetBrains Mono"'), document.fonts.load('600 46px Inter')]).catch(() => undefined);
  g.fillStyle = color; g.fillRect(0, 0, W, H);
  g.strokeStyle = 'rgba(255,255,255,0.16)'; g.lineWidth = 10;
  g.beginPath(); g.arc(W - 120, 260, 420, 0, Math.PI * 2); g.stroke();
  g.beginPath(); g.moveTo(W - 540, 260); g.lineTo(W + 300, 260); g.stroke();
  if (ctx.club?.logo_url) {
    const logo = await loadImg(ctx.club.logo_url).catch(() => null);
    if (logo) { g.save(); g.beginPath(); g.arc(150, 150, 90, 0, Math.PI * 2); g.closePath(); g.fillStyle = '#FFFFFF'; g.fill(); g.clip(); g.drawImage(logo, 60, 60, 180, 180); g.restore(); }
  }
  g.fillStyle = '#FFFFFF'; g.textBaseline = 'alphabetic';
  const top = story ? 520 : 400;
  g.font = '700 38px "JetBrains Mono", monospace';
  g.fillText(`${team.name} · ${c.kind === 'collective' ? 'DÉFI COLLECTIF' : 'DÉFI'}`.toUpperCase(), 72, top);
  // Le titre, sur deux lignes au plus.
  g.font = '400 120px Anton, Impact, sans-serif';
  const words = c.title.toUpperCase().split(/\s+/), lines: string[] = [];
  for (const w of words) { const cur = lines[lines.length - 1]; if (cur && g.measureText(`${cur} ${w}`).width < W - 144) lines[lines.length - 1] = `${cur} ${w}`; else lines.push(w); }
  lines.slice(0, 2).forEach((l, i) => g.fillText(i === 1 && lines.length > 2 ? `${l}…` : l, 64, top + 130 + i * 128));
  let y = top + 130 + Math.min(2, lines.length) * 128 + 40;
  if (c.kind === 'collective') {
    const col = collective(c, es);
    g.font = '400 230px Anton, Impact, sans-serif';
    g.fillText(num(col.total), 64, y + 200);
    g.font = '600 50px Inter, system-ui, sans-serif';
    g.fillText(`${c.target ? `sur ${num(c.target)} ` : ''}${c.unit ?? ''}`.trim(), 72, y + 280);
    g.fillText(col.done ? 'Objectif atteint, bravo à toute l’équipe !' : 'Merci à toute l’équipe !', 72, y + 360);
  } else {
    const podium = ranking(c, es).filter(x => x.rank <= 3).slice(0, 5);
    const medal = ['', '🥇', '🥈', '🥉'];
    for (const x of podium) {
      g.font = '80px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
      g.fillText(medal[x.rank] ?? '', 64, y + 110);
      g.font = '400 92px Anton, Impact, sans-serif';
      g.fillText(who(x.person_id).toUpperCase(), 180, y + 105);
      g.font = '700 46px "JetBrains Mono", monospace';
      g.textAlign = 'right'; g.fillText(fmtVal(c, x.best), W - 72, y + 100); g.textAlign = 'left';
      y += 150;
    }
  }
  g.font = '600 40px Inter, system-ui, sans-serif';
  g.fillText(ctx.club?.name ?? '', 72, H - 80);
  return new Promise((ok, ko) => cv.toBlob(b => (b ? ok(b) : ko(new Error('image'))), 'image/png'));
}

/** L'invitation d'une catégorie à un défi inter-catégories : accepter ou non, en un geste (son coach). */
function inviteCard(c: Challenge, rows: ChallengeBoardRow[], teamId: string): Html {
  const me = rows.find(r => r.team_id === teamId);
  const host = rows.find(r => r.is_host);
  const labels = boardLabels(rows);
  const lab = (id: string) => labels.get(id) ?? '';
  const g = (me?.gender ?? 'X') as TeamGender;
  const deadline = me ? new Date(Date.parse(me.invited_at) + 3 * 86400000) : null;
  const rules = c.kind === 'collective' ? (c.target ? `Objectif commun : ${fmtVal(c, c.target)}` : 'Objectif commun')
    : [c.mode !== 'simple' ? rulesLine(c) : '', tiersLine(c)].filter(Boolean).join(' · ');
  const desc = c.description?.trim() ? `${c.description.trim()}${/[.!?…]$/.test(c.description.trim()) ? '' : '.'} ` : '';
  const line = `${desc}${[rules, c.ends_on ? `jusqu'au ${shortDay(c.ends_on)}` : ''].filter(Boolean).join(' · ')}`.trim();
  return html`<section class="block df-inv">
    <p class="eyebrow eyebrow-accent">Défi inter-catégories · proposé par ${host?.launcher ?? '—'}${host ? ` (${lab(host.team_id)})` : ''}</p>
    <h3>${c.title}</h3>
    ${line ? html`<p class="small">${line}</p>` : ''}
    <div class="df-who">${rows.filter(r => r.status === 'accepte' || r.status === 'attente')
      .sort((a, b) => Number(a.status === 'attente') - Number(b.status === 'attente') || Number(a.team_id === teamId) - Number(b.team_id === teamId))
      .map(r => r.status === 'accepte'
      ? html`<span class="chip chip-ok">${r.name} ✓</span>`
      : r.team_id === teamId ? html`<span class="chip chip-warn">${lab(r.team_id)} · ${me?.mine ? 'ta réponse' : 'réponse de son coach'}</span>`
      : html`<span class="chip df-chip">${lab(r.team_id)} · en attente</span>`)}</div>
    ${me?.mine ? html`<div class="btn-row">
        <button type="button" class="btn btn-primary" data-df-ans="${c.id}" data-team="${teamId}" data-yes="1">Accepter pour les ${lab(teamId)}</button>
        <button type="button" class="btn btn-ghost" data-df-ans="${c.id}" data-team="${teamId}" data-yes="">Pas cette fois</button></div>
      <p class="hint">Accepté : tes ${playerNoun(g, 2)} sont ${agree(g, 'prévenu', 'prévenue', 2)}, et c’est toi qui valides leurs résultats.${deadline
        ? ` Sans réponse d’ici ${deadline.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })}, le défi part sans les ${lab(teamId)}.` : ''}</p>`
      : html`<p class="hint">C’est le coach des ${lab(teamId)} qui répond pour sa catégorie.</p>`}
  </section>`;
}

function wireAnswers(el: HTMLElement, onDone: () => void) {
  for (const b of $$<HTMLButtonElement>(el, '[data-df-ans]')) b.addEventListener('click', async () => {
    const yes = b.dataset.yes === '1';
    const btns = $$<HTMLButtonElement>(b.parentElement!, 'button');
    btns.forEach(x => { x.disabled = true; });
    try {
      await answerChallenge(b.dataset.dfAns!, b.dataset.team!, yes);
      toast(yes ? 'C’est parti : ton groupe est prévenu' : 'C’est noté : le défi continue sans ta catégorie');
      onDone();
    } catch { btns.forEach(x => { x.disabled = false; }); toast('Réponse impossible (délai passé ?)', 'ko'); }
  });
}

/** Valider les résultats de sa catégorie : « Valider / Refuser » pour chacun, ou « Tout valider » en un geste. */
async function openReview(c: Challenge, team: Team, onDone: () => void) {
  const rows = (await challengeReviewList(c.id).catch(() => [])).filter(r => r.team_id === team.id);
  const names = rankNames(rows);
  const g = team.gender;
  const author = (r: (typeof rows)[number]) => r.author_kind === 'self' ? (names.get(r.person_id) ?? r.author)
    : r.author_kind === 'mate' ? `${r.author} (${agree(g, 'coéquipier', 'coéquipière')})` : r.author_kind === 'parent' ? `${r.author} (parent)` : r.author;
  const state = new Map<string, 'ok' | 'ko'>();
  openSheet(`À valider · ${team.name}`, html`<div class="df-review">
    <p class="lead">${c.title} · seuls les résultats validés entrent au classement inter-catégories.</p>
    ${rows.length > 1 ? html`<button type="button" class="btn btn-ghost btn-sm df-all" id="df-all">✓ Tout valider (${rows.length})</button>` : ''}
    ${rows.length ? rows.map(r => html`<div class="df-row" data-entry="${r.entry_id}">
        <strong>${names.get(r.person_id)} · ${fmtVal(c, Number(r.value))}</strong>
        <span class="df-st2" role="group" aria-label="${names.get(r.person_id)}"><button type="button" data-v="ok" aria-pressed="false">Valider</button><button type="button" data-v="ko" aria-pressed="false">Refuser</button></span>
        <small>saisi par ${author(r)}, ${whenShort(r.created_at)}${r.note ? ` · ${r.note}` : ''}</small></div>`)
      : html`<p class="muted small">Rien à valider pour l’instant.</p>`}
    ${rows.length ? html`<button type="button" class="btn btn-primary df-save" id="df-save" disabled>Enregistrer</button>
      <p class="hint">Refusé : ${agree(g, 'le joueur', 'la joueuse')} est ${agree(g, 'prévenu', 'prévenue')} et peut noter un nouveau résultat.</p>` : ''}
  </div>`, (sheet, close) => {
    const save = $<HTMLButtonElement>(sheet, '#df-save');
    const plural = (n: number, w: string) => `${n} ${w}${n > 1 ? 's' : ''}`;
    const paint = () => {
      for (const row of $$(sheet, '[data-entry]')) {
        const s = state.get(row.dataset.entry!);
        for (const b of $$<HTMLButtonElement>(row, '[data-v]')) {
          const on = s === b.dataset.v, ok = b.dataset.v === 'ok';
          b.className = on ? `on ${b.dataset.v}` : '';
          b.textContent = `${on ? (ok ? '✓ ' : '✕ ') : ''}${ok ? 'Valider' : 'Refuser'}`;
          b.setAttribute('aria-pressed', String(on));
        }
      }
      const nOk = [...state.values()].filter(v => v === 'ok').length, nKo = state.size - nOk, rest = rows.length - state.size;
      if (save) {
        save.disabled = !state.size;
        save.textContent = state.size ? `Enregistrer · ${[nOk ? plural(nOk, 'validé') : '', nKo ? plural(nKo, 'refusé') : '', rest ? `${rest} à voir` : ''].filter(Boolean).join(', ')}` : 'Enregistrer';
      }
    };
    for (const b of $$<HTMLButtonElement>(sheet, '[data-v]')) b.addEventListener('click', () => {
      const id = b.closest<HTMLElement>('[data-entry]')!.dataset.entry!;
      const v = b.dataset.v as 'ok' | 'ko';
      if (state.get(id) === v) state.delete(id); else state.set(id, v);
      paint();
    });
    const send = async (ok: string[], ko: string[], btn: HTMLButtonElement) => {
      btn.disabled = true;
      try {
        const r = await reviewChallengeEntries(c.id, ok, ko);
        close();
        toast([r.valid ? `${plural(r.valid, 'résultat')} ${r.valid > 1 ? 'validés' : 'validé'}` : '', r.refused ? `${r.refused} refusé${r.refused > 1 ? 's' : ''} (${agree(g, 'prévenu', 'prévenue', r.refused)})` : ''].filter(Boolean).join(' · ') || 'Enregistré');
        onDone();
      } catch { btn.disabled = false; toast('Impossible pour le moment.', 'ko'); }
    };
    save?.addEventListener('click', () => void send([...state].filter(([, v]) => v === 'ok').map(([k]) => k), [...state].filter(([, v]) => v === 'ko').map(([k]) => k), save));
    const all = $<HTMLButtonElement>(sheet, '#df-all');
    all?.addEventListener('click', () => void send(rows.map(r => r.entry_id), [], all));
  });
}

// -----------------------------------------------------------------------------
// Lancer un défi : une idée en un geste, ou le formulaire (façon de noter, score, paliers, équipe engagée)
// -----------------------------------------------------------------------------
const DEFAULT_TIERS: TierDef[] = [{ name: 'Bronze', threshold: NaN }, { name: 'Argent', threshold: NaN }, { name: 'Or', threshold: NaN }];
const tierRow = (t: TierDef) => html`<div class="df-tier-row">
  <input name="tname" maxlength="24" value="${t.name}" aria-label="Nom du palier" placeholder="ex. Or">
  <input name="tval" type="number" inputmode="decimal" step="any" min="0" value="${Number.isFinite(t.threshold) ? String(t.threshold) : ''}" aria-label="Seuil du palier" placeholder="seuil">
  <button type="button" class="sheet-x" data-tier-del aria-label="Retirer ce palier">×</button></div>`;

async function openNewChallenge(ctx: Ctx, team: Team, onDone: () => void) {
  // Pour qui (D-095) : mon équipe, ou plusieurs catégories du club (même saison).
  const club = sortForPick(await challengeClubTeams(team.id).catch(() => [] as ClubTeamPick[]), team.id);
  const host: ClubTeamPick = club.find(t => t.id === team.id) ?? { id: team.id, name: team.name, short_name: null, category: team.category, gender: team.gender };
  const others = club.filter(t => t.id !== team.id);
  const labels = shortLabels([host, ...others]);
  const squads = ctx.squads.filter(s => s.team_id === team.id);
  const ideas = challengeIdeas(team.gender);
  const modeBtn = (m: ChallengeMode) => html`<button type="button" class="chip-toggle" data-mode="${m}" aria-pressed="${String(m === 'simple')}">${MODE_LABEL[m]}</button>`;
  const periodBtn = (p: CountPeriod) => html`<button type="button" role="radio" data-period="${p}" aria-pressed="${String(p === 'month')}">${PERIOD_LABEL[p]}</button>`;
  // Le défi Mains vives (D-165) : seulement là où le jeu caméra est ouvert.
  const mvHere = mvOpen(ctx, team.club_id);
  const modes: ChallengeMode[] = ['simple', 'series', 'chrono', 'rebours', 'presences', ...(mvHere ? ['mainsvives' as const] : [])];
  openSheet('Lancer un défi', html`
    <form class="form" id="f-ch" novalidate>
      <div class="field"><span>Une idée, en un geste</span>
        <div class="df-themes" role="group" aria-label="Thème des idées">${IDEA_THEMES.map((t, n) => html`<button type="button" class="chip-toggle" data-idea-theme="${t.key}" aria-pressed="${String(n === 0)}">${t.label}</button>`)}</div>
        <div class="df-ideas" role="list">${ideas.map(i => html`<button type="button" class="df-idea" role="listitem" data-idea="${i.id}" data-theme-of="${i.theme}" aria-pressed="false" ${i.theme === IDEA_THEMES[0]!.key ? '' : 'hidden'}><span aria-hidden="true">${i.icon}</span>${i.title}</button>`)}</div></div>
      <label class="field"><span>Défi</span><input name="title" maxlength="80" required placeholder="ex. Lancers francs sur 20"></label>
      <label class="field"><span>Règles (facultatif)</span><textarea name="description" rows="2" maxlength="600" placeholder="ex. 20 tentatives après l'entraînement, ${agree(team.gender, 'un coéquipier compte', 'une coéquipière compte')}"></textarea></label>
      ${others.length ? html`<div class="field"><span>Pour qui</span>
        <div class="seg" role="radiogroup" aria-label="Pour qui">
          <button type="button" role="radio" data-scope="team" aria-pressed="true">Mon équipe</button>
          <button type="button" role="radio" data-scope="multi" aria-pressed="false">Plusieurs catégories</button>
        </div></div>
        <div class="df-pick" hidden>
          <div class="df-cats">${[host, ...others].map(t => t.id === host.id
            ? html`<button type="button" class="chip-toggle df-lock" aria-pressed="true" disabled>✓ ${t.name}</button>`
            : html`<button type="button" class="chip-toggle" data-df-cat="${t.id}" aria-pressed="false">${t.name}</button>`)}</div>
          <p class="hint" id="df-pick-hint"></p>
        </div>` : ''}
      ${squads.length > 1 ? html`<label class="field df-squad"><span>Équipe engagée</span><select name="squad">
        <option value="">Tout le groupe</option>${squads.map(s => html`<option value="${s.id}">${s.name} seulement</option>`)}</select></label>` : ''}
      <div class="seg" role="radiogroup" aria-label="Type">
        <button type="button" role="radio" data-kind="individual" aria-pressed="true">${agree(team.gender, 'Chacun son score', 'Chacune son score')}</button>
        <button type="button" role="radio" data-kind="collective" aria-pressed="false">Objectif commun</button>
      </div>
      <div class="df-indiv">
        <div class="field"><span>Façon de noter</span><div class="chip-toggles df-modes">${modes.map(modeBtn)}</div></div>
        ${mvHere ? html`<div class="field df-m df-m-mainsvives" hidden data-guide="df-mv"><span>Rempli par le score d’une partie de Mains vives</span>
          <div class="seg" role="radiogroup" aria-label="Le score">${(['avg', 'hits'] as MvMeasure[]).map(m => html`<button type="button" role="radio" data-mvm="${m}" aria-pressed="${String(m === 'avg')}">${m === 'avg' ? 'Temps moyen' : 'Touchées'}</button>`)}</div>
          <div class="chip-toggles df-mvlv" role="group" aria-label="Le niveau">${LEVELS.map(l => html`<button type="button" class="chip-toggle" data-mvl="${l.n}" aria-pressed="${String(l.n === 1)}">Niveau ${l.n} · ${l.short}</button>`)}</div>
          <p class="hint" id="df-mv-hint"></p></div>` : ''}
        <div class="field df-m df-m-presences" hidden><span>Compté par l’app : présences à l’entraînement</span>
          <div class="seg" role="radiogroup" aria-label="Période">${(['month', 'quarter', 'season'] as CountPeriod[]).map(periodBtn)}</div>
          <p class="hint" id="df-period-hint"></p></div>
        <div class="row-2 df-m df-m-series" hidden>
          <label class="field"><span>Essais par série</span><input name="series_size" type="number" inputmode="numeric" min="1" max="500" step="1" value="10"></label>
          <label class="field"><span>Séries à noter</span><input name="series_count" type="number" inputmode="numeric" min="1" max="20" step="1" value="1"></label>
        </div>
        <label class="field df-m df-m-rebours" hidden><span>Durée (secondes)</span><input name="countdown_s" type="number" inputmode="numeric" min="5" max="3600" step="1" value="60"></label>
        <div class="row-2 df-score">
          <label class="field df-m df-m-simple"><span>Le meilleur</span><select name="better"><option value="higher">le plus haut</option><option value="lower">le plus court (chrono)</option></select></label>
          <label class="field"><span>Le score de ${agree(team.gender, 'chacun', 'chacune')}</span><select name="aggregate">
            <option value="best">son meilleur</option><option value="avg">sa moyenne</option><option value="sum">son total</option><option value="last">son dernier</option></select></label>
        </div>
        <div class="field"><span>Paliers (facultatif)</span><div class="df-tiers" id="df-tiers">${DEFAULT_TIERS.map(tierRow)}</div>
          <button type="button" class="link-x" id="df-tier-add">＋ un palier</button></div>
      </div>
      <label class="field df-unit"><span>Unité</span><input name="unit" maxlength="20" placeholder="ex. réussis, tirs, km"></label>
      <label class="field gl-target" hidden><span>Objectif commun</span><input name="target" type="number" step="any" min="1" placeholder="ex. 1000"></label>
      <label class="field df-ends"><span>Jusqu'au (facultatif)</span><input name="ends" type="date"></label>
      <p class="hint" id="df-end-hint">Avec une date, le défi se clôt tout seul le lendemain : podium et palmarès, l’équipe est prévenue.</p>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit" id="df-go">Lancer (le groupe est prévenu)</button>
    </form>`, (sheet, close) => {
    const f = $<HTMLFormElement>(sheet, '#f-ch')!;
    const go = $<HTMLButtonElement>(f, '#df-go')!;
    const input = (n: string) => f.elements.namedItem(n) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
    let kind: 'individual' | 'collective' = 'individual';
    let mode: ChallengeMode = 'simple';
    let scope: 'team' | 'multi' = 'team';
    let period: CountPeriod = 'month';
    let measure: MvMeasure = 'avg';
    let mvLevel: Level = 1;
    let idea: string | null = null;
    const picked = new Set<string>();
    const chosen = () => others.filter(t => picked.has(t.id));
    // Celles que j'entraîne moi-même partent tout de suite ; les autres attendent l'accord de leur coach.
    const toAsk = () => chosen().filter(t => !ctx.full.pureCoach.has(t.id));
    const refresh = () => {
      const sel = chosen(), ask = toAsk();
      const names = (ts: ClubTeamPick[]) => joinFr(ts.map(t => labels.get(t.id) ?? t.name));
      go.textContent = scope === 'team' ? 'Lancer (le groupe est prévenu)' : !sel.length ? 'Choisis au moins une autre catégorie'
        : ask.length ? `Proposer aux coachs des ${names(ask)}` : `Lancer pour les ${names([host, ...sel])}`;
      go.disabled = scope === 'multi' && !sel.length;
      const g: TeamGender = [host, ...sel].every(t => t.gender === 'F') ? 'F' : 'M';
      const hint = $(f, '#df-pick-hint');
      if (hint) hint.textContent = `Chaque coach des catégories choisies accepte d’un geste avant que le défi parte à son groupe. Il valide aussi les résultats de ses ${playerNoun(g, 2)}.`;
      const sq = $<HTMLElement>(f, '.df-squad');
      if (sq) sq.hidden = scope === 'multi';
      // Les présences : une seule équipe (pas pour plusieurs catégories).
      $<HTMLElement>(f, '[data-mode="presences"]')!.hidden = scope === 'multi';
      const mvBtn = $<HTMLElement>(f, '[data-mode="mainsvives"]');
      if (mvBtn) mvBtn.hidden = scope === 'multi';
      if (scope === 'multi' && (mode === 'presences' || mode === 'mainsvives')) setMode('simple');
    };
    // La période des présences, en dates (la base pose les mêmes) ; la saison : ses dates à elle.
    const periodHint = () => {
      const r = presenceRange(period, new Date());
      $(f, '#df-period-hint')!.textContent = `${r ? `${periodText(r.from, r.to).replace(/^d/, 'D').replace(/\.?$/, '.')} ` : 'Toute la saison. '}Chaque entraînement fini compte, d’après l’appel : personne ne note rien. Tu peux corriger pour ${agree(team.gender, 'un joueur', 'une joueuse')}.`;
    };
    const setKind = (k: typeof kind) => {
      kind = k;
      $$(f, '[data-kind]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.kind === k)));
      $(f, '.df-indiv')!.hidden = k === 'collective';
      $(f, '.gl-target')!.hidden = k !== 'collective';
      setMode(k === 'collective' ? 'simple' : mode);
    };
    const setMode = (m: ChallengeMode) => {
      mode = m;
      $$(f, '[data-mode]').forEach(x => x.setAttribute('aria-pressed', String(x.dataset.mode === m)));
      $$(f, '.df-m').forEach(x => { x.hidden = !x.classList.contains(`df-m-${m}`); });
      const auto = kind === 'individual' && m === 'presences';
      const mvc = kind === 'individual' && m === 'mainsvives';
      $(f, '.df-unit')!.hidden = kind === 'individual' && (m === 'chrono' || auto || mvc);
      if (mvc) mvHint();
      $(f, '.df-score')!.hidden = auto;
      $(f, '.df-ends')!.hidden = auto;
      $(f, '#df-end-hint')!.textContent = auto ? 'Le défi se clôt tout seul le lendemain de la fin de la période : podium et palmarès, l’équipe est prévenue.'
        : 'Avec une date, le défi se clôt tout seul le lendemain : podium et palmarès, l’équipe est prévenue.';
      if (auto) periodHint();
      // Au chrono (et au temps moyen de Mains vives), jamais de total.
      const noSum = m === 'chrono' || (mvc && measure === 'avg');
      const sum = $<HTMLOptionElement>(f, 'select[name="aggregate"] option[value="sum"]')!;
      sum.disabled = noSum;
      if (noSum && input('aggregate')!.value === 'sum') input('aggregate')!.value = 'best';
    };
    // Mains vives : ce que mesure le défi, et comment il se remplit.
    const mvHint = () => {
      const h = $(f, '#df-mv-hint');
      if (h) h.textContent = `${measure === 'avg' ? 'Le temps de réaction moyen : le plus bas gagne.' : 'Les cibles touchées : le plus haut gagne.'} ${agree(team.gender, 'Chacun', 'Chacune')} joue chez soi, téléphone posé, 60 s au niveau ${mvLevel} ; à la fin de la partie, ${agree(team.gender, 'il', 'elle')} envoie son score au défi (un parent pour les plus jeunes). Ni vidéo ni image.`;
    };
    const setTiers = (ts: TierDef[]) => { mount($(f, '#df-tiers')!, html`${ts.map(tierRow)}`); };
    refresh();
    for (const b of $$<HTMLButtonElement>(f, '[data-scope]')) b.addEventListener('click', () => {
      scope = b.dataset.scope as typeof scope;
      $$(f, '[data-scope]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      $(f, '.df-pick')!.hidden = scope !== 'multi';
      refresh();
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-df-cat]')) b.addEventListener('click', () => {
      const id = b.dataset.dfCat!;
      if (picked.has(id)) picked.delete(id); else picked.add(id);
      b.setAttribute('aria-pressed', String(picked.has(id)));
      b.textContent = `${picked.has(id) ? '✓ ' : ''}${others.find(t => t.id === id)?.name ?? ''}`;
      refresh();
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-kind]')) b.addEventListener('click', () => setKind(b.dataset.kind as typeof kind));
    for (const b of $$<HTMLButtonElement>(f, '[data-mode]')) b.addEventListener('click', () => setMode(b.dataset.mode as ChallengeMode));
    for (const b of $$<HTMLButtonElement>(f, '[data-mvm]')) b.addEventListener('click', () => {
      measure = b.dataset.mvm as MvMeasure;
      $$(f, '[data-mvm]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      setMode(mode);
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-mvl]')) b.addEventListener('click', () => {
      mvLevel = Number(b.dataset.mvl) as Level;
      $$(f, '[data-mvl]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      mvHint();
    });
    for (const b of $$<HTMLButtonElement>(f, '[data-period]')) b.addEventListener('click', () => {
      period = b.dataset.period as CountPeriod;
      $$(f, '[data-period]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      periodHint();
    });
    $(f, '#df-tier-add')!.addEventListener('click', () => {
      const box = $(f, '#df-tiers')!;
      if ($$(box, '.df-tier-row').length >= 10) return toast('Dix paliers au plus.', 'ko');
      box.insertAdjacentHTML('beforeend', tierRow({ name: '', threshold: NaN }).s);
    });
    f.addEventListener('click', (e) => { const b = (e.target as HTMLElement).closest('[data-tier-del]'); if (b) b.closest('.df-tier-row')?.remove(); });
    // Les idées par thème : un toucher sur un thème montre ses idées.
    for (const t of $$<HTMLButtonElement>(f, '[data-idea-theme]')) t.addEventListener('click', () => {
      $$(f, '[data-idea-theme]').forEach(x => x.setAttribute('aria-pressed', String(x === t)));
      for (const b of $$<HTMLButtonElement>(f, '[data-idea]')) b.hidden = b.dataset.themeOf !== t.dataset.ideaTheme;
    });
    // Une idée : tout le formulaire rempli d'un geste (le coach peut encore changer, puis « Lancer »).
    for (const b of $$<HTMLButtonElement>(f, '[data-idea]')) b.addEventListener('click', () => {
      const i: ChallengeIdea = ideas.find(x => x.id === b.dataset.idea)!;
      idea = i.id;
      $$(f, '[data-idea]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      input('title')!.value = i.title; input('description')!.value = i.description; input('unit')!.value = i.unit ?? '';
      setKind(i.kind);
      if (i.kind === 'individual') {
        setMode(i.mode);
        input('aggregate')!.value = i.aggregate;
        input('better')!.value = i.better ?? 'higher';
        if (i.series_size) input('series_size')!.value = String(i.series_size);
        if (i.series_count) input('series_count')!.value = String(i.series_count);
        if (i.countdown_s) input('countdown_s')!.value = String(i.countdown_s);
        setTiers(i.tiers?.length ? i.tiers : DEFAULT_TIERS);
      } else input('target')!.value = String(i.target ?? '');
      input('title')!.focus();
    });
    const numOf = (k: string) => { const v = String(input(k)?.value ?? '').replace(',', '.').trim(); return v === '' ? null : Number(v); };
    f.addEventListener('submit', async (e) => {
      e.preventDefault();
      const msg = (t: string) => { $(f, '.form-msg')!.textContent = t; };
      const title = String(input('title')?.value ?? '').trim();
      if (!title) return msg('Donne un nom au défi.');
      if (kind === 'collective' && !numOf('target')) return msg('Indique l’objectif commun.');
      const tiers: TierDef[] = [];
      for (const row of $$(f, '.df-tier-row')) {
        const name = ($<HTMLInputElement>(row, 'input[name="tname"]')!.value ?? '').trim();
        const raw = ($<HTMLInputElement>(row, 'input[name="tval"]')!.value ?? '').replace(',', '.').trim();
        if (!raw) continue;
        if (!name) return msg('Donne un nom à chaque palier qui a un seuil.');
        tiers.push({ name: name.slice(0, 24), threshold: Number(raw) });
      }
      if (new Set(tiers.map(t => t.name.toLowerCase())).size < tiers.length) return msg('Deux paliers ont le même nom.');
      const ind = kind === 'individual';
      const auto = ind && mode === 'presences';
      const mvc = ind && mode === 'mainsvives';
      const draft: ChallengeDraft = {
        team_id: team.id, title, description: String(input('description')?.value ?? '').trim() || null, kind,
        better: ind && mode === 'simple' ? input('better')!.value as 'higher' | 'lower' : mode === 'chrono' || (mvc && measure === 'avg') ? 'lower' : 'higher',
        unit: auto || mvc ? null : String(input('unit')?.value ?? '').trim() || null, bronze: null, silver: null, gold: null,
        mv_level: mvc ? mvLevel : null, mv_measure: mvc ? measure : null, mv_duration_s: mvc ? 60 : null,
        target: ind ? null : numOf('target'), ends_on: auto ? null : String(input('ends')?.value ?? '') || null,
        mode: ind ? mode : 'simple', aggregate: auto ? 'best' : ind ? input('aggregate')!.value as Aggregate : 'sum',
        count_period: auto ? period : null,
        series_size: ind && mode === 'series' ? Math.round(numOf('series_size') ?? 10) : null,
        series_count: ind && mode === 'series' ? Math.round(numOf('series_count') ?? 1) : null,
        countdown_s: ind && mode === 'rebours' ? Math.round(numOf('countdown_s') ?? 60) : null,
        tiers: ind && tiers.length ? tiers : null, squad_id: scope === 'team' ? (String(input('squad')?.value ?? '') || null) : null, idea,
      };
      go.disabled = true;
      try {
        if (scope === 'multi') {
          const ask = toAsk();
          const { squad_id: _sq, ...forInter } = draft;
          await launchInterChallenge(team.id, forInter as ChallengeDraft, chosen().map(t => t.id));
          close(); toast(ask.length ? 'Défi proposé : les coachs sont prévenus' : 'Défi lancé : les groupes sont prévenus');
        } else {
          await createChallenge(draft);
          close(); toast(draft.squad_id ? `Défi lancé : les ${playerNoun(team.gender, 2)} de ${squads.find(s => s.id === draft.squad_id)?.name ?? ''} sont ${agree(team.gender, 'prévenus', 'prévenues', 2)}` : 'Défi lancé');
        }
        onDone();
      } catch { go.disabled = false; msg('Impossible pour le moment.'); }
    });
  });
}

// -----------------------------------------------------------------------------
// Gages
// -----------------------------------------------------------------------------
async function renderForfeits(el: HTMLElement, teamId: string, staff: boolean, nameOf: Map<string, string>, mine: Set<string>, reload: () => void, kids: Set<string> = new Set()) {
  const [ideas, draws, authors] = await Promise.all([
    forfeitIdeas(teamId).catch(() => [] as ForfeitIdea[]), forfeitDraws(teamId).catch(() => [] as ForfeitDraw[]),
    staff ? forfeitIdeaAuthors(teamId).catch(() => []) : Promise.resolve([]),
  ]);
  const ideaOf = new Map(ideas.map(i => [i.id, i]));
  const authorOf = new Map(authors.map(a => [a.idea_id, a.author]));
  const myDraws = draws.filter(d => mine.has(d.person_id) && (d.status === 'a_tirer' || d.status === 'tire'));
  const debts = new Map<string, number>();
  for (const d of draws) if (d.status === 'passe') debts.set(d.person_id, (debts.get(d.person_id) ?? 0) + 1);
  const validated = ideas.filter(i => i.status === 'valide');
  const pending = ideas.filter(i => i.status === 'propose');
  const STATUS: Record<ForfeitDraw['status'], string> = { a_tirer: 'à tirer', tire: 'tiré, à décider', accepte: 'accepté', passe: 'passé (dette)', fait: 'fait' };

  mount(el, html`
    ${myDraws.map(d => html`<section class="block gl-draw">
      <span class="eyebrow eyebrow-accent">${kids.has(d.person_id) ? `Un gage pour ${nameOf.get(d.person_id) ?? ''}` : `${mine.size > 1 ? `${nameOf.get(d.person_id)} · ` : ''}Un gage pour toi`}${d.reason ? ` · ${d.reason}` : ''}</span>
      ${d.status === 'a_tirer' ? html`<button type="button" class="btn btn-primary" data-draw="${d.id}">${kids.has(d.person_id) ? 'Tirer le gage' : 'Tirer mon gage'}</button>`
        : html`<p class="gl-forfeit">${ideaOf.get(d.idea_id ?? '')?.text ?? 'Gage tiré'}</p>
          <div class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-dec="${d.id}" data-a="accept">${kids.has(d.person_id) ? `${nameOf.get(d.person_id) ?? ''} accepte` : 'J’accepte'}</button>
            <button type="button" class="btn btn-ghost btn-sm" data-dec="${d.id}" data-a="pass">${kids.has(d.person_id) ? `${nameOf.get(d.person_id) ?? ''} passe (une dette)` : 'Je passe (une dette)'}</button></div>`}
    </section>`)}
    ${staff ? html`<button type="button" class="btn btn-primary vol-ask" id="gl-give" ${validated.length ? '' : 'disabled'}>＋ Donner un gage</button>
      ${validated.length ? '' : html`<p class="hint">Valide d'abord des idées de gages (plus bas).</p>`}` : ''}
    <section class="block">
      <h3>Gages de l’équipe</h3>
      ${draws.length ? html`<ul class="ce-rows">${draws.slice(0, 30).map(d => html`<li><span><strong>${nameOf.get(d.person_id) ?? '—'}</strong>
        ${d.idea_id && d.status !== 'a_tirer' ? html` · ${ideaOf.get(d.idea_id)?.text ?? ''}` : ''}<br><span class="muted small">${STATUS[d.status]}${d.reason ? ` · ${d.reason}` : ''}</span></span>
        ${staff && (d.status === 'accepte' || d.status === 'tire') ? html`<button type="button" class="btn btn-ghost btn-sm" data-done="${d.id}">Fait</button>` : ''}
        ${staff && d.status === 'a_tirer' ? html`<button type="button" class="link-x" data-ddel="${d.id}">retirer</button>` : ''}</li>`)}</ul>`
        : html`<p class="muted small">Aucun gage pour l'instant.</p>`}
      ${debts.size ? html`<p class="small"><strong>Dettes :</strong> ${[...debts.entries()].map(([pid, n]) => `${nameOf.get(pid) ?? '—'} (${n})`).join(', ')}</p>` : ''}
    </section>
    <section class="block">
      <h3>Boîte à idées</h3>
      <p class="muted small">Propose un gage drôle et gentil : le staff valide avant qu'il entre dans le tirage. Ton nom n'est pas montré au reste de l’équipe.</p>
      <form class="inline-form" id="f-idea" novalidate>
        <input name="text" maxlength="200" placeholder="ex. Chanter l'hymne du club au prochain entraînement" aria-label="Idée de gage">
        <button class="btn btn-ghost btn-sm" type="submit">Proposer</button>
      </form>
      ${staff && pending.length ? html`<h4 class="sub">À valider · ${pending.length}</h4><ul class="ce-rows">${pending.map(i => html`
        <li><span>${i.text}<br><span class="muted small">par ${authorOf.get(i.id) ?? '—'}</span></span>
          <span class="btn-row"><button type="button" class="btn btn-primary btn-sm" data-iv="${i.id}" data-s="valide">Valider</button>
          <button type="button" class="btn btn-ghost btn-sm" data-iv="${i.id}" data-s="refuse">Refuser</button></span></li>`)}</ul>` : ''}
      ${validated.length ? html`<details class="alt-box"><summary>Gages validés · ${validated.length}</summary><ul class="ce-rows">${validated.map(i => html`
        <li><span>${i.text}</span>${staff ? html`<button type="button" class="link-x" data-idel="${i.id}">retirer</button>` : ''}</li>`)}</ul></details>` : ''}
    </section>`);

  const fi = $<HTMLFormElement>(el, '#f-idea')!;
  fi.addEventListener('submit', async (e) => {
    e.preventDefault();
    const t = String(new FormData(fi).get('text') ?? '').trim();
    if (t.length < 3) return;
    try { await proposeForfeit(teamId, t); toast(staff ? 'Idée ajoutée (à valider)' : 'Merci ! Le staff va la regarder'); reload(); } catch { toast('Impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-iv]')) b.addEventListener('click', async () => {
    try { await setForfeitIdea(b.dataset.iv!, b.dataset.s as 'valide' | 'refuse'); reload(); } catch { toast('Impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-idel]')) b.addEventListener('click', async () => { try { await deleteForfeitIdea(b.dataset.idel!); reload(); } catch { toast('Impossible.', 'ko'); } });
  for (const b of $$<HTMLButtonElement>(el, '[data-done]')) b.addEventListener('click', async () => { try { await forfeitDone(b.dataset.done!); toast('Gage fait'); reload(); } catch { toast('Impossible.', 'ko'); } });
  for (const b of $$<HTMLButtonElement>(el, '[data-ddel]')) b.addEventListener('click', async () => { try { await deleteForfeitDraw(b.dataset.ddel!); reload(); } catch { toast('Impossible.', 'ko'); } });
  $(el, '#gl-give')?.addEventListener('click', () => {
    openSheet('Donner un gage', html`
      <form class="form" id="f-give" novalidate>
        <label class="field"><span>À qui</span><select name="person">${[...nameOf.entries()].map(([id, n]) => html`<option value="${id}">${n}</option>`)}</select></label>
        <label class="field"><span>Motif (facultatif)</span><input name="reason" maxlength="120" placeholder="ex. retard, téléphone pendant la causerie"></label>
        <button class="btn btn-primary" type="submit">Donner (tirage au sort dans l’app)</button>
      </form>`, (sheet, close) => {
      const f = $<HTMLFormElement>(sheet, '#f-give')!;
      f.addEventListener('submit', async (e) => {
        e.preventDefault();
        const d = new FormData(f);
        try { await giveForfeit(teamId, String(d.get('person')), String(d.get('reason') ?? '').trim() || null); close(); toast('Gage donné : la personne est prévenue'); reload(); }
        catch { toast('Impossible.', 'ko'); }
      });
    });
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-draw]')) b.addEventListener('click', () => openDraw(b.dataset.draw!, reload));
  for (const b of $$<HTMLButtonElement>(el, '[data-dec]')) b.addEventListener('click', async () => {
    b.disabled = true;
    try { await forfeitPlay(b.dataset.dec!, b.dataset.a as 'accept' | 'pass'); toast(b.dataset.a === 'accept' ? 'Courage !' : 'Passé : c’est noté en dette'); reload(); }
    catch { b.disabled = false; toast('Impossible.', 'ko'); }
  });
}

/** Le tirage : 3, 2, 1… puis le gage. */
function openDraw(drawId: string, onDone: () => void) {
  const host = document.createElement('div');
  host.className = 'pk-over gl-over';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', 'Tirage du gage');
  document.body.append(host);
  const close = () => { host.remove(); onDone(); };
  mount(host, html`<div class="pk-stage"><p class="eyebrow">Tirage au sort</p><p class="gl-count" id="gl-count" aria-live="assertive">3</p></div>`);
  let n = 3;
  const tick = async () => {
    n -= 1;
    const c = $(host, '#gl-count');
    if (n > 0) { if (c) c.textContent = String(n); setTimeout(() => void tick(), 800); return; }
    try {
      const r = await forfeitPlay(drawId, 'draw');
      mount(host, html`<div class="pk-stage">
        <p class="eyebrow">Ton gage</p>
        <p class="gl-reveal">${r.text ?? ''}</p>
        <div class="btn-row"><button type="button" class="btn btn-primary" id="gl-acc">J'accepte</button><button type="button" class="btn btn-ghost" id="gl-pass">Je passe (une dette)</button></div>
      </div>`);
      $(host, '#gl-acc')!.addEventListener('click', async () => { await forfeitPlay(drawId, 'accept').catch(() => undefined); toast('Courage !'); close(); });
      $(host, '#gl-pass')!.addEventListener('click', async () => { await forfeitPlay(drawId, 'pass').catch(() => undefined); toast('Passé : c’est noté en dette'); close(); });
    } catch (e) {
      toast(String((e as Error).message).includes('aucun') ? 'Aucun gage validé pour l’instant.' : 'Tirage impossible.', 'ko');
      close();
    }
  };
  setTimeout(() => void tick(), 800);
}

/** Accueil des joueuses et parents (une ligne d'« À faire », D-155) : un gage à tirer, sinon le défi en cours (de l'équipe,
 *  de mon équipe engagée, ou inter-catégories). */
export async function renderGroupCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  const teams = workTeams(ctx).filter(t => !ctx.staffOf.has(t.id));
  if (!teams.length) return;
  const mine = new Set(ctx.people.map(p => p.id));
  for (const t of teams) {
    const draws = (await forfeitDraws(t.id).catch(() => [] as ForfeitDraw[])).filter(d => mine.has(d.person_id) && (d.status === 'a_tirer' || d.status === 'tire'));
    if (draws.length) {
      mount(el, html`<a class="card card-row" href="#/defis/${t.id}/gages"><span class="play-thumb" aria-hidden="true"><span class="ti ti-gift"></span></span>
        <span class="card-main"><span class="eyebrow eyebrow-accent">Défis et gages</span><span class="card-title">Un gage à tirer !</span>
        <span class="muted small">${t.name}${draws[0]!.reason ? ` · ${draws[0]!.reason}` : ''}</span></span><span class="occ-go" aria-hidden="true">›</span></a>`);
      return;
    }
    // Un défi réservé à une équipe engagée (D-151) : seulement si l'une de mes fiches y joue.
    const mySquads = ctx.memberships.filter(m => m.team_id === t.id && mine.has(m.person_id)).map(m => m.squad_ids ?? []);
    const forMe = (c: Challenge) => !c.squad_id || mySquads.some(s => !s.length || s.includes(c.squad_id!));
    const open = (await challenges(t.id).catch(() => [] as Challenge[])).filter(c => !c.closed_at && c.team_status !== 'attente' && forMe(c));
    if (open.length) {
      const c = open[0]!;
      mount(el, html`<a class="card card-row" href="#/defis/${t.id}/defis"><span class="play-thumb" aria-hidden="true"><span class="ti ti-trophy"></span></span>
        <span class="card-main"><span class="eyebrow eyebrow-accent">${c.inter ? 'Défi inter-catégories' : 'Défi en cours'}${open.length > 1 ? ` · ${open.length}` : ''}</span><span class="card-title">${c.title}</span>
        <span class="muted small">${t.name} · ${c.inter ? 'relève-le, ton coach valide ton résultat' : 'relève-le et grimpe au classement'}</span></span><span class="occ-go" aria-hidden="true">›</span></a>`);
      return;
    }
  }
}

/** Accueil du coach (D-095) : les défis inter-catégories qui attendent sa réponse (accepter en un geste),
 *  puis les résultats de ses joueuses à valider. */
export async function renderChallengeHome(el: HTMLElement, ctx: Ctx): Promise<void> {
  const ids = workTeams(ctx).filter(t => ctx.staffOf.has(t.id)).map(t => t.id);
  const [inv, pend] = await Promise.all([pendingChallengeTeams(ids).catch(() => []), pendingChallengeEntries(ids).catch(() => [])]);
  const chIds = [...new Set([...inv.map(i => i.challenge_id), ...pend.map(p => p.challenge_id)])];
  if (!chIds.length) { mount(el, html``); return; }
  const [list, board] = await Promise.all([challengesByIds(chIds).catch(() => [] as Challenge[]), challengeBoard(chIds).catch(() => [] as ChallengeBoardRow[])]);
  // Seulement là où c'est à moi de répondre et de valider (le coach de la catégorie).
  const isMine = (cid: string, tid: string) => board.some(b => b.challenge_id === cid && b.team_id === tid && b.mine);
  const invites = inv.filter(i => isMine(i.challenge_id, i.team_id) && list.some(c => c.id === i.challenge_id && !c.closed_at));
  const checks = new Map<string, { c: Challenge; teamId: string; n: number }>();
  for (const p of pend) {
    const c = list.find(x => x.id === p.challenge_id);
    if (!c || !isMine(p.challenge_id, p.team_id)) continue;
    const k = `${p.challenge_id}:${p.team_id}`;
    checks.set(k, { c, teamId: p.team_id, n: (checks.get(k)?.n ?? 0) + 1 });
  }
  const labels = boardLabels(board);
  mount(el, html`
    ${invites.map(i => inviteCard(list.find(c => c.id === i.challenge_id)!, board.filter(b => b.challenge_id === i.challenge_id), i.team_id))}
    ${[...checks.values()].map(x => html`<a class="card card-row" href="#/defis/${x.teamId}/defis"><span class="play-thumb" aria-hidden="true"><span class="ti ti-trophy"></span></span>
      <span class="card-main"><span class="eyebrow eyebrow-accent">Défi inter-catégories · à valider</span><span class="card-title">${x.c.title}</span>
      <span class="muted small">${x.n > 1 ? `${x.n} résultats des ${labels.get(x.teamId)} attendent` : `1 résultat des ${labels.get(x.teamId)} attend`} ta validation</span></span>
      <span class="occ-go" aria-hidden="true">›</span></a>`)}`);
  wireAnswers(el, () => void renderChallengeHome(el, ctx));
}

// -----------------------------------------------------------------------------
// Anniversaires (D-151) : le jour J, dans l'équipe ; le prénom seulement, sans messages ni mur
// -----------------------------------------------------------------------------
/** « Joyeux anniversaire Léa ! » sur la page d'une équipe (avec le module « Défis et gages »). */
export async function renderTeamBirthdays(el: HTMLElement, ctx: Ctx, team: Team): Promise<void> {
  if (!modOn(ctx, 'defis', team.club_id)) return;
  const rows = await birthdaysToday([team.id]).catch(() => []);
  if (!rows.length) return;
  mount(el, html`<p class="df-bday" data-guide="team-bday"><span aria-hidden="true">🎂</span> Joyeux anniversaire ${joinFr(rows.map(r => r.name))} !</p>`);
}

/** L'accueil (« Le club ») : les anniversaires du jour dans mes équipes, une carte vers l'équipe. */
export async function renderHomeBirthdays(el: HTMLElement, ctx: Ctx): Promise<void> {
  const teams = workTeams(ctx).filter(t => modOn(ctx, 'defis', t.club_id));
  const rows = await birthdaysToday(teams.map(t => t.id)).catch(() => []);
  if (!rows.length) return;
  const byTeam = new Map<string, string[]>();
  for (const r of rows) byTeam.set(r.team_id, [...(byTeam.get(r.team_id) ?? []), r.name]);
  const [teamId, names] = [...byTeam.entries()][0]!;
  const team = teams.find(t => t.id === teamId);
  const more = byTeam.size > 1 ? ` · et ${byTeam.size - 1} autre${byTeam.size > 2 ? 's' : ''} équipe${byTeam.size > 2 ? 's' : ''}` : '';
  mount(el, html`<a class="card card-row" href="#/t/${teamId}"><span class="play-thumb" aria-hidden="true">🎂</span>
    <span class="card-main"><span class="eyebrow eyebrow-accent">Anniversaire</span><span class="card-title">Joyeux anniversaire ${joinFr(names)} !</span>
    <span class="muted small">${team?.name ?? ''}${more}</span></span><span class="occ-go" aria-hidden="true">›</span></a>`);
}

/** Moi : mon anniversaire (et celui de mes enfants), affiché le jour J dans l'équipe, ou masqué. */
export async function renderBirthdayRow(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!modOn(ctx, 'defis')) return;
  const mine = await myBirthdays().catch(() => []);
  if (!mine.length) return;
  const sub = (rs: typeof mine) => (rs.every(r => r.hidden) ? 'Masqué' : rs.length === 1 ? (rs[0]!.hidden ? 'Masqué' : 'Affiché le jour J dans l’équipe') : `${rs.filter(r => !r.hidden).length} sur ${rs.length} affichés le jour J`);
  mount(el, html`<button type="button" class="card card-row" id="me-bday" data-guide="me-bday">
    <span class="mi-ico" aria-hidden="true">🎂</span>
    <span class="card-main"><strong>${mine.length === 1 && mine[0]!.self ? 'Mon anniversaire' : 'Anniversaires'}</strong><span class="muted small" id="me-bday-sub">${sub(mine)}</span></span>
    <span class="occ-go" aria-hidden="true">›</span></button>`);
  $(el, '#me-bday')!.addEventListener('click', () => {
    const day = (r: (typeof mine)[number]) => new Date(2000, r.month - 1, r.day).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    openSheet('Anniversaire', html`<div class="form">
      <p class="muted">Le jour J, l’équipe voit « Joyeux anniversaire » et le prénom, jamais l’âge ni l’année. Pas de messages.</p>
      ${mine.map(r => html`<label class="check"><input type="checkbox" data-bday="${r.person_id}" ${r.hidden ? '' : 'checked'}>
        <span><strong>${r.self ? 'Afficher mon anniversaire' : `Afficher l’anniversaire de ${r.name}`}</strong><br><span class="muted small">le ${day(r)}</span></span></label>`)}
    </div>`, (sheet) => {
      for (const b of $$<HTMLInputElement>(sheet, '[data-bday]')) b.addEventListener('change', async () => {
        try {
          await setBirthdayHidden(b.dataset.bday!, !b.checked);
          const r = mine.find(x => x.person_id === b.dataset.bday);
          if (r) r.hidden = !b.checked;
          const s = $(el, '#me-bday-sub'); if (s) s.textContent = sub(mine);
          toast(b.checked ? 'Affiché le jour J' : 'Masqué');
        } catch { b.checked = !b.checked; toast('Impossible pour le moment.', 'ko'); }
      });
    });
  });
}
